/**
 * The type badge, and the menu behind it.
 *
 * ── Two paths to the same five types, and both are keyboard-operable ─────────────────────────
 * The badge itself CYCLES on click — R → W → T → B → A → R, the entity's own
 * `nextSetTypeInCycle` — because relabelling a row is the commonest edit in the table and a menu
 * for it would be two gestures for one decision. The menu beside it PICKS directly, which is what
 * a trainer wants when the type they need is three steps away, and it is the only place the
 * back-off's question can be asked.
 *
 * The badge is a button, so the cycle is Enter or Space. The menu is Base UI's, so it is the arrow
 * keys, Home/End and type-ahead. Neither path is a reduced version of the other: the cycle can
 * reach every type the menu can.
 *
 * ── Back-off is the entry that is not a relabel ──────────────────────────────────────────────
 * A descent is a percentage OF something, so the top has to be resolved before the row can become
 * one — which is why `changeSetType` refuses it and `makeBackoffSet` exists. On a CYCLE the answer
 * is always `null`: this row becomes the descent, and if the exercise has no top yet a fresh
 * unloaded one appears above it. Nothing the trainer authored is repurposed, and the badge shows
 * the B it promised. In the MENU the choice is named — bind an existing set, or put a new top above
 * — because there the difference can be seen before it is taken.
 */

import { Menu } from '@base-ui/react/menu';

import {
  BACKOFF_SET_TYPE,
  changeSetType,
  makeBackoffSet,
  type SetAddress,
} from '~/entities/plan-draft';

import type { SetBadgeView, SetTypeChoiceView } from '../model/dayProgramming';

import menuStyles from '~/shared/ui/MenuSurface.module.css';
import styles from './SetTable.module.css';

const BADGE_CLASS: Record<SetBadgeView['kind'], string> = {
  warmup: 'badgeWarmup',
  working: 'badgeWorking',
  top: 'badgeTop',
  backoff: 'badgeBackoff',
  amrap: 'badgeAmrap',
  dropset: 'badgeDropset',
  raw: 'badgeRaw',
};

/**
 * The badge where nothing can be done with it: a viewer, and a change under review. The letter still
 * has to say what the row IS, so it keeps its name and its colour; it simply stops being a control.
 * A `<button>` here would be an affordance for an act the surface does not offer, and
 * `aria-disabled` on one would put a dead stop in the table's tab order.
 */
export function SetTypeBadge({ badge }: { readonly badge: SetBadgeView }) {
  return (
    <span
      className={`${String(styles.badge)} ${String(styles[BADGE_CLASS[badge.kind]] ?? '')}`}
      role="img"
      aria-label={badge.label}
      title={badge.label}
    >
      {badge.letter}
    </span>
  );
}

interface SetTypeMenuProps {
  readonly badge: SetBadgeView;
  readonly choices: readonly SetTypeChoiceView[];
  readonly address: SetAddress;
  readonly menuLabel: string;
  /**
   * "Typ serii" — the group heading the popup prints over its five rows.
   *
   * The mock draws it, and the parameter menu one control away already prints its own
   * ("PARAMETRY SERII"), so a popup here with no heading read as an omission rather than as a
   * choice.
   */
  readonly menuTitle: string;
  readonly announce: (message: string) => void;
  /** The day is being READ. The badge stays — it names the row — and both paths into it go. */
  readonly readOnly: boolean;
}

export function SetTypeMenu({
  badge,
  choices,
  address,
  menuLabel,
  menuTitle,
  announce,
  readOnly,
}: SetTypeMenuProps) {
  /**
   * The one dispatch both paths go through. It takes only what a type change NEEDS — the type, the
   * top it binds to, and the words to say afterwards — rather than a whole menu row, because the
   * badge's cycle is not a menu row and building a fake one to satisfy the signature was how this
   * function started asking for a letter and a description it never reads.
   */
  const apply = (choice: Pick<SetTypeChoiceView, 'label' | 'setType'> & { topSetIndex?: number | null }) => {
    if (choice.setType === BACKOFF_SET_TYPE) {
      makeBackoffSet(address, choice.topSetIndex ?? null);
    } else {
      changeSetType(address, choice.setType);
    }
    announce(choice.label);
  };

  if (readOnly) {
    return (
      <span className={styles.rowControls}>
        <SetTypeBadge badge={badge} />
      </span>
    );
  }

  /**
   * The BADGE is the trigger, and the cycle is gone (8 September 2026).
   *
   * The mock's `TYP` column is 30px and holds one control: the badge, which opens the menu. Ours was
   * 46px and held two — the badge cycled R → W → T → B → A and a caret beside it opened the picker —
   * which is 16px of a 574px panel spent on a second path to an act the menu already names better.
   * Nothing is lost but the shortcut: every type the cycle reached is a row in the popup, and the
   * popup is the one that says what picking it does before it is taken.
   */
  return (
    <span className={styles.rowControls}>
      <Menu.Root>
        <Menu.Trigger
          className={`${String(styles.badge)} ${String(styles[BADGE_CLASS[badge.kind]] ?? '')}`}
          // The letter is a glyph, so the accessible name has to be the words.
          aria-label={badge.label}
          title={badge.label}
        >
          <span aria-hidden="true">{badge.letter}</span>
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Positioner className={menuStyles.positioner} sideOffset={6} align="start">
            <Menu.Popup
              className={`${String(menuStyles.popup)} ${String(styles.typePopup)}`}
              aria-label={menuLabel}
            >
              <span className={menuStyles.groupLabel}>{menuTitle}</span>
              {choices.map((choice) => (
                <Menu.Item
                  key={choice.key}
                  className={`${String(styles.typeItem)} ${choice.isCurrent ? String(styles.typeItemOn) : ''} ${choice.isRefused ? String(styles.typeItemRefused) : ''}`}
                  // The name, the line under it and — when it applies — the word "current", as ONE
                  // accessible name the model wrote. The description is not decoration: it is the
                  // difference between "Seria backoff" and knowing what picking it does, and a
                  // row's explanation reaching only the eye is how a menu ends up navigable but not
                  // usable.
                  aria-label={choice.ariaLabel}
                  // A refused row stays on the menu and under the keyboard — the reason IS its
                  // description — but a click takes nothing and leaves the menu open: closing on a
                  // dead press would read as the pick having happened.
                  aria-disabled={choice.isRefused || undefined}
                  closeOnClick={!choice.isRefused}
                  onClick={() => {
                    if (choice.isRefused) return;
                    apply(choice);
                  }}
                >
                  <span
                    className={`${String(styles.typeBadge)} ${String(styles[BADGE_CLASS[choice.kind]] ?? '')}`}
                    aria-hidden="true"
                  >
                    {choice.letter}
                  </span>
                  <span className={styles.typeMid}>
                    {/* `aria-hidden` on both: the row's own `aria-label` already carries them, and
                      * without this a screen reader reads the name and the description twice. */}
                    <span className={styles.typeName} aria-hidden="true">
                      {choice.label}
                    </span>
                    <span className={styles.typeDesc} aria-hidden="true">
                      {choice.description}
                    </span>
                  </span>
                  {/* No tick. The mock draws none, and the current row is already named as current
                    * in `choice.ariaLabel` — the channel that was never colour. */}
                </Menu.Item>
              ))}
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>
    </span>
  );
}

